import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  actorId: string | null;
  occurredAt: string | null;
  beforeState: string | null;
  afterState: string | null;
}

/**
 * Story 10.11 — audit log reader (TENANT_ADMIN only).
 *
 * SALES_MANAGER and below will hit a 403 from the backend when they
 * request /analytics/audit, and the route is also guarded client-side
 * via the stored role. CSV export lives here too — v1 generates it in
 * the browser from the loaded rows.
 */
export function AuditLogPage() {
  const role = useAuthStore((s) => s.user?.role);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [entityType, setEntityType] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const { data } = await apiClient.get<AuditEntry[]>('/analytics/audit', {
        params: entityType ? { entityType } : undefined,
      });
      setEntries(data);
    } catch {
      setError('No autorizado');
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function exportCsv() {
    const header = 'id,action,entityType,entityId,actorId,occurredAt\n';
    const rows = entries
      .map(
        (e) =>
          `${e.id},${e.action},${e.entityType},${e.entityId},${e.actorId ?? ''},${e.occurredAt ?? ''}`,
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (role && role !== 'TENANT_ADMIN' && role !== 'PLATFORM_ADMIN') {
    return <p className="text-sm text-destructive">Acceso denegado</p>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Audit log</h1>
      <div className="flex gap-2">
        <Input
          placeholder="Filtrar por entity_type"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="max-w-sm"
        />
        <Button size="sm" onClick={refresh}>
          Aplicar
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv}>
          Exportar CSV
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Acción</th>
              <th className="px-3 py-2">Entidad</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">Fecha</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="px-3 py-2">{e.action}</td>
                <td className="px-3 py-2">
                  {e.entityType} · {e.entityId.slice(0, 8)}…
                </td>
                <td className="px-3 py-2">{e.actorId?.slice(0, 8) ?? '—'}</td>
                <td className="px-3 py-2">{e.occurredAt?.slice(0, 19) ?? '—'}</td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
